import { lazy, Suspense, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { INFRA_KINDS, newId, type InfraKind } from '@gk/contracts'
import { areaLabel, useCourse } from '../data/geo.ts'
import { useLocationOn } from '../data/location.ts'
import { approxLengthM, boundsOf, formatLength, type GeoJsonGeometry, type Pos } from '../lib/geo.ts'
import { DrawTool } from '../map/DrawTool.tsx'
import { useBaseMap } from '../map/useBaseMap.ts'
import { BackLink, Banner, Field, PendingBanner } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { InfraPhotos } from './map/InfraPanel.tsx'

const MapView = lazy(() => import('../map/MapView.tsx'))
const SOURCES = ['digitised_ortho', 'survey_gnss', 'as_built', 'sketch'] as const
const LINE_KINDS: InfraKind[] = ['pipe', 'cable']
const isLine = (k: string) => (LINE_KINDS as string[]).includes(k)

/** Position of an underground service or irrigation item, captured honestly: a one-shot GPS fix keeps its own accuracy, a spot placed on the aerial
 *  photo says it was placed there, a sketch says it is a sketch. The old position is kept when it is corrected. */
export function InfraEditPage() {
  const { t, i18n } = useTranslation()
  const params = useParams({ strict: false }) as { featureId?: string }
  const search = useSearch({ strict: false }) as { area?: string }
  const navigate = useNavigate()
  const edit = useWriteAccess('infra.edit')
  const course = useCourse(i18n.language)
  const base = useBaseMap()
  const w = useWorkshopAction()
  const featureId = params.featureId
  const item = featureId ? course.infra.find((i) => i.id === featureId) : undefined
  const current = featureId ? course.infraVersions.find((v) => v.feature_id === featureId && /,\s*\)$/.test(v.valid_during)) : undefined

  const newFeatureId = useRef(newId()); const versionId = useRef(newId())
  const [kind, setKind] = useState<InfraKind>((item?.kind as InfraKind) ?? 'valve')
  const [label, setLabel] = useState(item?.label ?? '')
  const [code, setCode] = useState(item?.code ?? '')
  const [areaId, setAreaId] = useState(item?.area_id ?? search.area ?? '')
  const [note, setNote] = useState(item?.note ?? '')
  const [depth, setDepth] = useState(current?.depth_m != null ? String(current.depth_m) : '')
  const initialPts = useMemo<Pos[]>(() => {
    const g = current?.geom
    if (!g) return []
    if (g.type === 'Point') return [g.coordinates as Pos]
    if (g.type === 'LineString') return (g.coordinates as Pos[]).map((p) => [p[0], p[1]] as Pos)
    return []
  }, [current?.geom])
  const [pts, setPts] = useState<Pos[] | null>(null)
  const points = pts ?? initialPts
  const [source, setSource] = useState<(typeof SOURCES)[number] | 'device_gps'>('digitised_ortho')
  const [accuracy, setAccuracy] = useState('2')
  const [gps, setGps] = useState<{ accuracy: number } | 'asking' | 'denied' | null>(null)
  // M21: where the club has location off, the phone's position is never asked for: the point is placed on the map only
  const location = useLocationOn()
  const [sure, setSure] = useState(false); const [reason, setReason] = useState('')
  const fit = useMemo(() => boundsOf([current?.geom, ...course.areaCollection.features.map((f) => f.geometry)]), [current?.geom, course.areaCollection])

  if (featureId && course.ready && !item) return <Banner tone="error">{t('map.itemMissing')}</Banner>
  if (!course.ready) return <p>{t('common.loading')}</p>
  const line = isLine(kind)
  const need = line ? 2 : 1
  const moved = pts !== null
  const hasPosition = points.length >= need
  const accNum = accuracy.trim() === '' ? null : Number(accuracy.replace(',', '.'))
  const accOk = source === 'sketch' || (accNum !== null && Number.isFinite(accNum) && accNum >= 0 && accNum <= 1000)
  const depthNum = depth.trim() === '' ? null : Number(depth.replace(',', '.'))
  const depthOk = depthNum === null || (Number.isFinite(depthNum) && depthNum >= 0 && depthNum <= 20)
  const geometry = (): GeoJsonGeometry => (line ? { type: 'LineString', coordinates: points } : { type: 'Point', coordinates: points[0]! })

  // a kind the drawn position does not suit asks for a new position; going back to a suitable kind brings the recorded one back
  const changeKind = (k: InfraKind) => { if (isLine(k) !== line) { setPts(item && isLine(k) !== isLine(item.kind) ? [] : null); setGps(null) } setKind(k) }
  const useMyPosition = () => {
    if (!location) return
    if (!('geolocation' in navigator)) { setGps('denied'); return }
    setGps('asking')
    navigator.geolocation.getCurrentPosition(   // one-shot, on purpose: the position is a record, not a track
      (p) => { setPts([[p.coords.longitude, p.coords.latitude]]); setSource('device_gps'); setAccuracy(String(Math.round(p.coords.accuracy * 10) / 10)); setGps({ accuracy: p.coords.accuracy }) },
      () => setGps('denied'), { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 })
  }
  const versionPayload = () => ({
    version_id: versionId.current, geom: geometry(), source, ...(source !== 'sketch' ? { accuracy_m: accNum! } : {}), ...(depthNum !== null ? { depth_m: depthNum } : {}),
  })

  const save = async () => {
    if (!item) {
      const ok = await w.run('infra.create@1', {
        feature_id: newFeatureId.current, kind, label: label.trim(), ...(code.trim() ? { code: code.trim() } : {}), ...(areaId ? { area_id: areaId } : {}), ...(note.trim() ? { note: note.trim() } : {}),
        version: versionPayload(),
      } as never, 'infra.saved')
      if (ok) void navigate({ to: '/map', search: { infra: newFeatureId.current } })
      return
    }
    const set: Record<string, unknown> = {}
    if (kind !== item.kind) set.kind = kind
    if (label.trim() !== item.label) set.label = label.trim()
    if ((code.trim() || null) !== (item.code ?? null)) set.code = code.trim() || null
    if ((areaId || null) !== (item.area_id ?? null)) set.area_id = areaId || null
    if ((note.trim() || null) !== (item.note ?? null)) set.note = note.trim() || null
    if (Object.keys(set).length) {
      const ok = await w.run('infra.update@1', { feature_id: item.id, expected_revision: item.revision, set } as never, 'infra.saved', { aggregateId: item.id })
      if (!ok) return
    }
    if (moved || depthNum !== (current?.depth_m ?? null)) {
      const geom = moved ? geometry() : current!.geom
      const ok = await w.run('infra.revise@1', { feature_id: item.id, ...versionPayload(), geom } as never, 'infra.saved', { aggregateId: item.id })
      if (!ok) return
    }
    void navigate({ to: '/map', search: { infra: item.id } })
  }
  const retire = async () => {
    const ok = await w.run('infra.retire@1', { feature_id: item!.id, ...(reason.trim() ? { reason: reason.trim() } : {}) }, 'infra.retired')
    if (ok) void navigate({ to: '/map' })
  }
  const dirty = !item || moved || kind !== item.kind || label.trim() !== item.label || (code.trim() || null) !== (item.code ?? null) || (areaId || null) !== (item.area_id ?? null) || (note.trim() || null) !== (item.note ?? null) || depthNum !== (current?.depth_m ?? null)
  const canSave = edit.allowed && label.trim() && (item ? true : hasPosition) && (moved ? hasPosition && accOk : true) && depthOk && dirty && !w.busy
  const areaOptions = course.areas.filter((a) => !a.archived_at && a.area_type_code !== 'site_root')

  return (
    <section className="flex flex-col gap-3" data-testid="infra-page">
      <BackLink to="/map">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{item ? t('infra.editTitle', { name: item.label }) : t('infra.newTitle')}</h1>
      {!edit.permitted && <Banner tone="warn">{t('infra.notAllowed')}</Banner>}
      {edit.readOnlyMode && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('infra.kind')} htmlFor="infra-kind"><select id="infra-kind" className="input" value={kind} onChange={(e) => changeKind(e.target.value as InfraKind)} data-testid="infra-kind">{INFRA_KINDS.map((k) => <option key={k} value={k}>{t(`infraKind.${k}`)}</option>)}</select></Field>
        <Field label={t('infra.code')} htmlFor="infra-code"><input id="infra-code" className="input" maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} data-testid="infra-code" /></Field>
      </div>
      <Field label={t('infra.label')} htmlFor="infra-label"><input id="infra-label" className="input" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="infra-label" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('infra.area')} htmlFor="infra-area"><select id="infra-area" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)} data-testid="infra-area"><option value="">{t('infra.noArea')}</option>{areaOptions.map((a) => <option key={a.id} value={a.id}>{areaLabel(a, i18n.language)}</option>)}</select></Field>
        <Field label={t('infra.depth')} htmlFor="infra-depth" error={depthOk ? null : t('infra.depthRange')}><input id="infra-depth" className="input" inputMode="decimal" value={depth} onChange={(e) => setDepth(e.target.value)} data-testid="infra-depth" /></Field>
      </div>
      <Field label={t('infra.note')} htmlFor="infra-note"><input id="infra-note" className="input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="infra-note" /></Field>
      {/* M16-E: the feature's photo (M17's portrait pattern: the newest wins, nothing is deleted), for a feature that exists; a new one takes its photo from the map once created */}
      {item && <InfraPhotos featureId={item.id} retired={!!item.retired_at || !!item._pending} title={t('map.infraPanel.photoTitle')} />}

      <h2 className="text-lg font-bold">{t('infra.position')}</h2>
      <p className="text-sm opacity-75">{line ? t('infra.drawLineHelp') : t('infra.placeHelp')}</p>
      {!line && edit.allowed && location && (
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn !min-h-11" onClick={useMyPosition} disabled={gps === 'asking'} data-testid="infra-gps">{gps === 'asking' ? t('infra.gpsWait') : t('infra.useMyPosition')}</button>
          {gps && typeof gps === 'object' && <span className="text-sm font-semibold" data-testid="infra-gps-result">{t('infra.gpsResult', { m: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(gps.accuracy) })}</span>}
          {gps === 'denied' && <span className="text-sm text-red-700" data-testid="infra-gps-denied">{t('infra.gpsDenied')}</span>}
        </div>
      )}
      <Suspense fallback={<div className="grid h-[50dvh] place-items-center rounded-xl border">{t('common.loading')}</div>}>
        <MapView className="h-[50dvh] min-h-72 overflow-hidden rounded-xl border-2" base={base.archive} areas={course.areaCollection} infra={course.infraCollection}
          selected={item ? { kind: 'infra', id: item.id } : null} fit={fit} interactive={false}>
          {edit.allowed && <DrawTool mode={line ? 'line' : 'point'} value={points} onChange={(p) => { setPts(p); if (gps) setGps(null); if (source === 'device_gps') { setSource('digitised_ortho'); setAccuracy('2') } }} />}
        </MapView>
      </Suspense>
      <p className="text-sm" data-testid="infra-live">{line ? t('infra.points', { count: points.length }) + (points.length >= 2 ? ` · ≈ ${formatLength(approxLengthM(points), i18n.language)}` : '') : points[0] ? `${points[0][1].toFixed(6)}, ${points[0][0].toFixed(6)}` : t('infra.noPoint')}</p>
      {edit.allowed && (
        <div className="flex flex-wrap gap-2">
          <button className="btn !min-h-11" disabled={points.length === 0} onClick={() => setPts(points.slice(0, -1))} data-testid="infra-undo">{t('shape.undo')}</button>
          <button className="btn !min-h-11" disabled={points.length === 0} onClick={() => setPts([])} data-testid="infra-clear">{t('shape.clear')}</button>
        </div>
      )}
      {(moved || !item) && edit.allowed && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('shape.source')} htmlFor="infra-source">
            <select id="infra-source" className="input" value={source} disabled={source === 'device_gps'} onChange={(e) => setSource(e.target.value as typeof source)} data-testid="infra-source">
              {source === 'device_gps' && <option value="device_gps">{t('geoSource.device_gps')}</option>}
              {SOURCES.map((s) => <option key={s} value={s}>{t(`geoSource.${s}`)}</option>)}
            </select>
          </Field>
          {source !== 'sketch' && <Field label={t('shape.accuracy')} hint={source === 'device_gps' ? t('infra.accuracyGps') : t('shape.accuracyHelp')} htmlFor="infra-accuracy"><input id="infra-accuracy" className="input" inputMode="decimal" value={accuracy} readOnly={source === 'device_gps'} onChange={(e) => setAccuracy(e.target.value)} data-testid="infra-accuracy" /></Field>}
        </div>
      )}
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error"><span data-testid="infra-error">{w.error}</span></Banner>}
      {w.ok && <Banner tone="ok"><span data-testid="infra-ok">{w.ok}</span></Banner>}
      {edit.allowed && <button className="btn btn-primary !min-h-12" disabled={!canSave} onClick={() => void save()} data-testid="infra-save">{item ? t('common.save') : t('infra.create')}</button>}
      {item && edit.allowed && !item.retired_at && (
        <div className="card flex flex-col gap-2">
          <Field label={t('infra.retireReason')} htmlFor="infra-reason"><input id="infra-reason" className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="infra-reason" /></Field>
          {sure
            ? <button className="btn btn-danger !min-h-11 self-start" disabled={w.busy} onClick={() => void retire()} data-testid="infra-retire-confirm">{t('infra.retireConfirm')}</button>
            : <button className="btn !min-h-11 self-start" onClick={() => setSure(true)} data-testid="infra-retire">{t('infra.retire')}</button>}
        </div>
      )}
    </section>
  )
}
